import { CoordinateDto } from '@flyfreely-portal-ui/flyfreely';
import { bbox } from '@turf/bbox';
import { bearing } from '@turf/bearing';
import { buffer } from '@turf/buffer';
import { center } from '@turf/center';
import { circle } from '@turf/circle';
import {
    bearingToAzimuth,
    earthRadius,
    feature,
    point
} from '@turf/helpers';
import jsts from '@turf/jsts';
import { length } from '@turf/length';
import { geoAzimuthalEquidistant } from 'd3-geo';
import {
    BBox,
    Feature,
    GeoJsonProperties,
    LineString,
    Polygon,
    Position
} from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import { LngLat } from 'mapbox-gl';
import { FeatureProperties } from './constants';
import { FeatureType, MapFeature, MapGeometry } from './interfaces';

export function toLngLat(coordinate: CoordinateDto): LngLat {
    return new LngLat(coordinate.longitude, coordinate.latitude);
}

export function pointToLngLat(point: Position): LngLat {
    return new LngLat(point[0], point[1]);
}

export function getMeasurementDescription(
    geoJson: Feature<LineString>
): string {
    const kms = length(geoJson);
    const coords = geoJson.geometry.coordinates;
    if (coords.length >= 2) {
        const start = point(coords[0]);
        const end = point(coords[coords.length - 1]);
        const azimuth = bearingToAzimuth(bearing(start, end));
        const miles = toNauticalMiles(kms);
        return `${kms.toFixed(2)} km \n ${miles.toFixed(
            2
        )} nm \n ${azimuth.toFixed(0)}° T`;
    }
    return getMeasurementDescriptionForKms(kms);
}

export function getMeasurementDescriptionForKms(kms: number): string {
    const miles = toNauticalMiles(kms);
    return `${kms.toFixed(2)} km \n ${miles.toFixed(2)} nm`;
}

export function toPosition(coordinate: CoordinateDto): Position {
    return [coordinate.longitude, coordinate.latitude];
}

export function toNauticalMiles(kms: number): number {
    return kms / 1.852;
}

export function boundsOf(geom: GeoJSON.Geometry): mapboxgl.LngLatBounds {
    const boundingBox = bbox(geom);
    return boundsOfBoundingBox(boundingBox);
}

export function boundsOfBoundingBox(boundingBox: BBox) {
    if (
        !isFinite(boundingBox[0]) ||
        !isFinite(boundingBox[1]) ||
        !isFinite(boundingBox[2]) ||
        !isFinite(boundingBox[3])
    ) {
        return new mapboxgl.LngLatBounds();
    }

    return new mapboxgl.LngLatBounds([
        boundingBox[0],
        boundingBox[1],
        boundingBox[2],
        boundingBox[3]
    ]);
}

export function emptyMapFeature(type: FeatureType): MapGeometry {
    switch (type) {
        case 'LineString':
            return {
                type: 'LineString',
                coordinates: []
            };

        case 'Point':
            return {
                type: 'Point',
                coordinates: []
            };

        case 'Polygon':
            return {
                type: 'Polygon',
                coordinates: []
            };
    }
}

export function getMeasurementProperties(
    features: Feature[],
    points: Feature[]
) {
    let isMeasurementLine = false;
    let isMeasurementLabel = false;
    let isMeasurementRadius = false;
    let isMeasurementRadiusLine = false;
    let featureId = '';

    features.forEach((feature: Feature) => {
        switch (feature.properties.meta) {
            case FeatureProperties.MEASUREMENT_LINE:
                // when e.points.length > 1, the user has clicked on the starting point
                // in this case, DrawSingleMeasurementLineSelect changes
                // mode to PointSelect to be able to drag the starting point.
                isMeasurementLine = points.length === 0;
                featureId = feature.id.toString();
                return;
            case FeatureProperties.MEASUREMENT_LABEL:
                isMeasurementLabel = true;
                featureId = feature.properties.relatedFeatureIds[0];
                return;
            case FeatureProperties.MEASUREMENT_RADIUS:
                isMeasurementRadius = true;
                featureId = feature.id.toString();
                return;
            case FeatureProperties.MEASUREMENT_RADIUS_LINE:
                isMeasurementRadiusLine = true;
                featureId = feature.properties.relatedFeatureIds[0];
                return;
        }
    });
    return {
        featureId,
        isMeasurementLine,
        isMeasurementLabel,
        isMeasurementRadius,
        isMeasurementRadiusLine
    };
}

export function createBufferFromLine(
    lineGeoJson: LineString,
    distanceMeters: number
) {
    if (lineGeoJson.coordinates.length < 2) {
        return null;
    }

    const lineBuffer = buffer(lineGeoJson, distanceMeters, {
        units: 'meters'
    });

    return lineBuffer;
}

const { BufferOp, GeoJSONReader, GeoJSONWriter } = jsts;

/**
 * Corners sharper than this mitre ratio are bevelled by JTS instead of
 * spiking out indefinitely. Only bites below roughly 23 degrees of interior
 * angle.
 */
const MITRE_LIMIT = 5;

type AzimuthalProjection = ReturnType<typeof geoAzimuthalEquidistant>;

function projectCoords(coords: any, projection: AzimuthalProjection): any {
    return typeof coords[0] !== 'object'
        ? projection(coords)
        : coords.map((c: any) => projectCoords(c, projection));
}

function unprojectCoords(coords: any, projection: AzimuthalProjection): any {
    return typeof coords[0] !== 'object'
        ? projection.invert(coords)
        : coords.map((c: any) => unprojectCoords(c, projection));
}

function coordsIsNaN(coords: any): boolean {
    return Array.isArray(coords[0]) ? coordsIsNaN(coords[0]) : isNaN(coords[0]);
}

/**
 * Buffer a polygon by a distance in metres, keeping its corners sharp so that
 * the shape is preserved - a triangle stays a triangle. A positive distance
 * expands outward, a negative one shrinks inward.
 *
 * Returns null when the result cannot be represented as a single polygon:
 * shrinking past the shape's inscribed radius leaves JTS with nothing, and
 * shrinking a waisted shape past its narrowest point splits it into a
 * MultiPolygon. Callers keep the current geometry in both cases, which is what
 * stops an over-shrink from destroying the shape.
 *
 * @turf/buffer cannot express the sharp corners: it coerces a falsy `steps` to
 * 8 and rejects anything <= 0, so it can only ever produce round joins.
 * Passing a negative quadrantSegments straight to JTS selects JOIN_MITRE
 * instead. The projection round trip mirrors what @turf/buffer does
 * internally, so this shares its geodesy with createBufferFromLine above.
 */
export function bufferPolygon(polygon: Polygon, distanceMeters: number) {
    if (!isFinite(distanceMeters)) {
        return null;
    }

    const centre = center(polygon).geometry.coordinates;
    const projection = geoAzimuthalEquidistant()
        .rotate([-centre[0], -centre[1]])
        .scale(earthRadius);

    const buffered = new GeoJSONWriter().write(
        BufferOp.bufferOp(
            new GeoJSONReader().read({
                type: 'Polygon',
                coordinates: projectCoords(polygon.coordinates, projection)
            }),
            distanceMeters,
            -MITRE_LIMIT
        )
    );

    // JTS can return a degenerate result, or a MultiPolygon which MapGeometry
    // does not support
    if (buffered?.type !== 'Polygon' || coordsIsNaN(buffered.coordinates)) {
        return null;
    }

    return feature(<Polygon>{
        type: 'Polygon',
        coordinates: unprojectCoords(buffered.coordinates, projection)
    });
}

export function createCircleFromPoint(
    center: Position,
    radiusMetres: number
) {
    return circle(center, radiusMetres, {
        units: 'meters',
        steps: 64
    });
}

export function getRadiusDescription(radiusMetres: number) {
    return `${radiusMetres} m`;
}

export class MapMeasurementCircleFeature implements MapFeature {
    id: number;
    name: string;
    centerCoordinates: number[];
    radiusKm: number;
    properties: GeoJsonProperties;
    geom: MapGeometry;
    children: MapFeature[];

    constructor(
        id: number,
        name: string,
        centerCoordinates: number[],
        radiusKm: number
    ) {
        this.id = id;
        this.name = name;
        this.centerCoordinates = centerCoordinates;
        this.radiusKm = radiusKm;
        this.setProperties();
    }

    private setProperties() {
        const circleFeature = circle(this.centerCoordinates, this.radiusKm);
        this.geom = circleFeature.geometry;

        const id = this.id + 1000;

        const lineGeom: LineString = {
            type: 'LineString',
            coordinates: [this.centerCoordinates, this.geom.coordinates[0][0]]
        };
        this.children = [
            {
                id: id,
                geom: lineGeom,
                name: `${name}_measurement`,
                properties: {
                    meta: FeatureProperties.MEASUREMENT_RADIUS_LINE,
                    measurement: getMeasurementDescription(feature(lineGeom)),
                    parent: this.id,
                    relatedFeatureIds: [this.id]
                }
            }
        ];

        this.properties = {
            center: this.centerCoordinates,
            radiusInKm: this.radiusKm,
            meta: FeatureProperties.MEASUREMENT_RADIUS,
            relatedFeatureIds: [this.children.map(c => c.id)]
        };
    }
}
export class MapMeasurementLineFeature implements MapFeature {
    id: number;
    name: string;
    coordinates: number[][];
    geom: LineString;
    properties: GeoJsonProperties;
    children: MapFeature[];

    constructor(id: number, name: string, coordinates: number[][]) {
        this.id = id;
        this.name = name;
        this.coordinates = coordinates;
        this.geom = {
            type: 'LineString',
            coordinates: coordinates
        };
        this.properties = {
            meta: FeatureProperties.MEASUREMENT_LINE
        };
        this.setChildren();
        this.properties.relatedFeatureIds = [this.children.map(c => c.id)];
    }
    private setChildren() {
        const id = this.id + 1000;
        const start = this.coordinates[0];
        const end = this.coordinates[this.coordinates.length - 1];
        const lineBearing = bearing(start, end);
        this.children = [
            {
                id: id,
                geom: {
                    type: 'Point',
                    coordinates: end
                },
                name: `${name}_measurement`,
                properties: {
                    meta: FeatureProperties.MEASUREMENT_LABEL,
                    measurement: getMeasurementDescription(feature(this.geom)),
                    parent: this.id,
                    relatedFeatureIds: [this.id]
                }
            },
            {
                id: this.id + 2000,
                geom: {
                    type: 'Point',
                    coordinates: start
                },
                name: `${name}_origin`,
                properties: {
                    meta: FeatureProperties.MEASUREMENT_LINE_ORIGIN
                }
            },
            {
                id: this.id + 3000,
                geom: {
                    type: 'Point',
                    coordinates: end
                },
                name: `${name}_end`,
                properties: {
                    meta: FeatureProperties.MEASUREMENT_LINE_END,
                    bearing: lineBearing
                }
            }
        ];
    }
}
